import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence, LayoutGroup } from 'framer-motion';
import { Search, SlidersHorizontal, X, ChevronDown, ChevronLeft, ChevronRight, ArrowUpDown, DollarSign, Ruler, Gem, Palette, LayoutGrid, List, Grid2x2, Grid3x3, Rows3, Gift, MoreHorizontal, RefreshCw } from 'lucide-react';
import ArtworkCard from '@/components/ArtworkCard';
import { useAdminStore } from '@/lib/admin-store';
import { useAppStore } from '@/lib/store';
import { useTranslation } from '@/hooks/useTranslation';
import type { SizeClass, Rarity, Category } from '@/types';

const SIZE_OPTIONS: Array<{ id: SizeClass; label: string; desc: string; full: string }> = [
  { id: 'small', label: 'S', desc: '< 40cm', full: 'Small (<40cm)' },
  { id: 'medium', label: 'M', desc: '40–100', full: 'Medium (40–100cm)' },
  { id: 'large', label: 'L', desc: '> 100', full: 'Large (>100cm)' },
];

const RARITY_OPTIONS: Array<{ id: Rarity; label: string; icon: string; desc: string }> = [
  { id: 'unique', label: 'Unique', icon: '◆', desc: 'One of a kind' },
  { id: 'limited', label: 'Limited', icon: '◈', desc: 'Limited run' },
  { id: 'open', label: 'Open', icon: '◇', desc: 'Open edition' },
];

const PRICE_RANGES = [
  { label: '< $100', full: 'Under $100', min: 0, max: 10000 },
  { label: '$100–500', full: '$101 – $500', min: 10100, max: 50000 },
  { label: '$500–1K', full: '$501 – $1,000', min: 50100, max: 100000 },
  { label: '$1K+', full: 'Over $1,000', min: 100100, max: Infinity },
];

const SORT_OPTIONS = [
  { id: 'recommended', label: 'Recommended' },
  { id: 'price-asc', label: 'Price: Low to High' },
  { id: 'price-desc', label: 'Price: High to Low' },
  { id: 'newest', label: 'Newest First' },
] as const;

const DEFAULT_SHOP: { defaultSort: string; defaultGridColumns: 2 | 3 | 4; visibleFilters: { priceRange: boolean; size: boolean; rarity: boolean; category: boolean } } = {
  defaultSort: 'recommended',
  defaultGridColumns: 3,
  visibleFilters: { priceRange: true, size: true, rarity: true, category: true },
};

const ShopPage: React.FC = () => {
  const { t, localize } = useTranslation();
  const { currency, setCurrency } = useAppStore();
  const allArtworks = useAdminStore((s) => s.artworks);
  const allArtists = useAdminStore((s) => s.artists);
  const managedCategories = useAdminStore((s) => s.categories);
  const rawShopSettings = useAdminStore((s) => s.settings?.shopSettings);
  const shopSettings = {
    defaultSort: rawShopSettings?.defaultSort ?? DEFAULT_SHOP.defaultSort,
    defaultGridColumns: rawShopSettings?.defaultGridColumns ?? DEFAULT_SHOP.defaultGridColumns,
    visibleFilters: {
      priceRange: rawShopSettings?.visibleFilters?.priceRange ?? true,
      size: rawShopSettings?.visibleFilters?.size ?? true,
      rarity: rawShopSettings?.visibleFilters?.rarity ?? true,
      category: rawShopSettings?.visibleFilters?.category ?? true,
    },
  };
  const searchRef = useRef<HTMLInputElement>(null);

  // Active managed categories sorted by display order
  const activeCategories = useMemo(() =>
    managedCategories
      .filter((c) => c.isActive)
      .sort((a, b) => a.displayOrder - b.displayOrder),
    [managedCategories],
  );

  const MAX_VISIBLE_CATEGORIES = 7;

  const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null);
  const [activeSubcategoryId, setActiveSubcategoryId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [sortBy, setSortBy] = useState<string>(shopSettings.defaultSort);
  const [sortOpen, setSortOpen] = useState(false);
  const [moreCatsOpen, setMoreCatsOpen] = useState(false);
  const moreCatsRef = useRef<HTMLDivElement>(null);
  const moreCatsBtnRef = useRef<HTMLButtonElement>(null);
  const sortBtnRef = useRef<HTMLButtonElement>(null);
  const mobileCatsRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [selectedSize, setSelectedSize] = useState<SizeClass | null>(null);
  const [selectedRarity, setSelectedRarity] = useState<Rarity | null>(null);
  const [selectedPriceRange, setSelectedPriceRange] = useState<number | null>(null);
  const [selectedArtistId, setSelectedArtistId] = useState<string | null>(null);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [filterView, setFilterView] = useState<'cards' | 'list'>('cards');
  const [gridCols, setGridCols] = useState<2 | 3 | 4>(shopSettings.defaultGridColumns);

  // Helper: get position for portal dropdown relative to a button ref
  const getDropdownPos = useCallback((btnRef: React.RefObject<HTMLElement | null>, align: 'left' | 'right' = 'right') => {
    if (!btnRef.current) return { top: 0, left: 0 };
    const rect = btnRef.current.getBoundingClientRect();
    return {
      top: rect.bottom + 8,
      left: align === 'right' ? rect.right : rect.left,
    };
  }, []);

  // Mobile categories scroll detection
  const checkMobileScroll = useCallback(() => {
    const el = mobileCatsRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
  }, []);

  useEffect(() => {
    const el = mobileCatsRef.current;
    if (!el) return;
    checkMobileScroll();
    el.addEventListener('scroll', checkMobileScroll, { passive: true });
    window.addEventListener('resize', checkMobileScroll);
    return () => {
      el.removeEventListener('scroll', checkMobileScroll);
      window.removeEventListener('resize', checkMobileScroll);
    };
  }, [checkMobileScroll, activeCategories]);

  const scrollMobileCats = useCallback((dir: 'left' | 'right') => {
    const el = mobileCatsRef.current;
    if (!el) return;
    el.scrollBy({ left: dir === 'left' ? -120 : 120, behavior: 'smooth' });
  }, []);

  // Active category object for subcategory display
  const selectedCategory: Category | undefined = activeCategoryId
    ? activeCategories.find((c) => c._id === activeCategoryId)
    : undefined;

  const activeSubcategories = useMemo(() =>
    selectedCategory
      ? selectedCategory.subcategories.filter((s) => s.isActive).sort((a, b) => a.displayOrder - b.displayOrder)
      : [],
    [selectedCategory],
  );

  const GRID_OPTIONS: Array<{ cols: 2 | 3 | 4; icon: React.ReactNode; label: string }> = [
    { cols: 2, icon: <Grid2x2 size={12} />, label: '2 cols' },
    { cols: 3, icon: <Grid3x3 size={12} />, label: '3 cols' },
    { cols: 4, icon: <Rows3 size={12} className="rotate-90" />, label: '4 cols' },
  ];

  const gridClass = gridCols === 2
    ? 'grid-cols-1 sm:grid-cols-2'
    : gridCols === 3
      ? 'grid-cols-2 md:grid-cols-3'
      : 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4';

  const activeFilterCount = [selectedSize, selectedRarity, selectedPriceRange, selectedArtistId].filter(
    (v) => v !== null,
  ).length;

  const clearFilters = (): void => {
    setSelectedSize(null);
    setSelectedRarity(null);
    setSelectedPriceRange(null);
    setSelectedArtistId(null);
    setActiveCategoryId(null);
    setActiveSubcategoryId(null);
    setSearchQuery('');
    setSortBy('recommended');
  };

  useEffect(() => {
    if (!sortOpen) return;
    const handleClick = (): void => setSortOpen(false);
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [sortOpen]);

  // Close "More categories" dropdown on outside click
  useEffect(() => {
    if (!moreCatsOpen) return;
    const handleClick = (e: MouseEvent): void => {
      if (moreCatsRef.current && !moreCatsRef.current.contains(e.target as Node)) {
        setMoreCatsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [moreCatsOpen]);

  const filteredItems = useMemo(() => {
    let items = [...allArtworks].filter((a) => a.isForSale);
    // Category filter (by categoryId)
    if (activeCategoryId) {
      items = items.filter((a) => a.categoryId === activeCategoryId);
      // Subcategory filter
      if (activeSubcategoryId) {
        items = items.filter((a) => a.subcategoryId === activeSubcategoryId);
      }
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      items = items.filter((a) => {
        const artistName = typeof a.artist === 'object' && a.artist !== null
          ? ((a.artist as { name?: { en?: string; ka?: string } }).name?.en?.toLowerCase() || '') + ((a.artist as { name?: { en?: string; ka?: string } }).name?.ka?.toLowerCase() || '')
          : '';
        return a.title.en.toLowerCase().includes(q) || a.title.ka.toLowerCase().includes(q) || a.category.toLowerCase().includes(q) || artistName.includes(q);
      });
    }
    if (selectedArtistId) {
      items = items.filter((a) => {
        const artId = typeof a.artist === 'object' && a.artist !== null ? (a.artist as { _id?: string })._id : a.artist;
        return artId === selectedArtistId;
      });
    }
    if (selectedSize) items = items.filter((a) => a.sizeClass === selectedSize);
    if (selectedRarity) items = items.filter((a) => a.rarity === selectedRarity);
    if (selectedPriceRange !== null) {
      const range = PRICE_RANGES[selectedPriceRange];
      if (range) items = items.filter((a) => a.price.amount >= range.min && a.price.amount <= range.max);
    }
    if (sortBy === 'price-asc') items.sort((a, b) => a.price.amount - b.price.amount);
    else if (sortBy === 'price-desc') items.sort((a, b) => b.price.amount - a.price.amount);
    else if (sortBy === 'newest') items.sort((a, b) => b.year - a.year);
    return items;
  }, [activeCategoryId, activeSubcategoryId, searchQuery, sortBy, selectedSize, selectedRarity, selectedPriceRange, selectedArtistId, allArtworks]);

  const currentSort = SORT_OPTIONS.find((s) => s.id === sortBy);

  /* =====================================================
     VIEW A — Creative cards with animated backgrounds
     ===================================================== */
  const cardsView = (
    <div className="space-y-8">
      {/* Currency */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <div className="w-7 h-7 rounded-lg bg-black/[0.04] dark:bg-white/[0.06] flex items-center justify-center">
            <RefreshCw size={13} className="text-black/50 dark:text-white/50" />
          </div>
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-black/50 dark:text-white/50">{t.shop.currency || 'Currency'}</span>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {(['USD', 'GEL'] as const).map((cur) => (
            <button
              key={cur}
              onClick={() => setCurrency(cur)}
              className={`relative px-3 py-3.5 rounded-2xl text-center transition-all duration-300 overflow-hidden ${
                currency === cur
                  ? 'bg-black dark:bg-white text-white dark:text-black shadow-lg scale-[1.02]'
                  : 'bg-black/[0.03] dark:bg-white/[0.04] text-black/50 dark:text-white/50 hover:bg-black/[0.06] dark:hover:bg-white/[0.08]'
              }`}
            >
              {currency === cur && (
                <motion.div layoutId="currency-glow" className="absolute inset-0 bg-black dark:bg-white rounded-2xl" transition={{ duration: 0.3, ease: [0.76, 0, 0.24, 1] }} />
              )}
              <span className="relative z-10 text-[11px] font-bold">{cur === 'USD' ? '$ USD' : '₾ GEL'}</span>
            </button>
          ))}
        </div>
      </div>
      {/* Price */}
      {shopSettings.visibleFilters.priceRange && <div>
        <div className="flex items-center gap-2 mb-4">
          <div className="w-7 h-7 rounded-lg bg-black/[0.04] dark:bg-white/[0.06] flex items-center justify-center">
            <DollarSign size={13} className="text-black/50 dark:text-white/50" />
          </div>
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-black/50 dark:text-white/50">{t.shop.price}</span>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {PRICE_RANGES.map((range, idx) => (
            <button
              key={range.label}
              onClick={() => setSelectedPriceRange(selectedPriceRange === idx ? null : idx)}
              className={`relative px-3 py-3.5 rounded-2xl text-center transition-all duration-300 overflow-hidden ${
                selectedPriceRange === idx
                  ? 'bg-black dark:bg-white text-white dark:text-black shadow-lg scale-[1.02]'
                  : 'bg-black/[0.03] dark:bg-white/[0.04] text-black/50 dark:text-white/50 hover:bg-black/[0.06] dark:hover:bg-white/[0.08] hover:text-black/80 dark:hover:text-white/80'
              }`}
            >
              {selectedPriceRange === idx && (
                <motion.div layoutId="price-glow" className="absolute inset-0 bg-black dark:bg-white rounded-2xl" transition={{ duration: 0.3, ease: [0.76, 0, 0.24, 1] }} />
              )}
              <span className="relative z-10 text-[11px] font-bold">{range.label}</span>
            </button>
          ))}
        </div>
      </div>}

      {/* Size */}
      {shopSettings.visibleFilters.size && <div>
        <div className="flex items-center gap-2 mb-4">
          <div className="w-7 h-7 rounded-lg bg-black/[0.04] dark:bg-white/[0.06] flex items-center justify-center">
            <Ruler size={13} className="text-black/50 dark:text-white/50" />
          </div>
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-black/50 dark:text-white/50">{t.shop.size}</span>
        </div>
        <div className="flex gap-2">
          {SIZE_OPTIONS.map((opt) => (
            <button
              key={opt.id}
              onClick={() => setSelectedSize(selectedSize === opt.id ? null : opt.id)}
              className={`flex-1 relative flex flex-col items-center gap-1 py-4 rounded-2xl transition-all duration-300 overflow-hidden ${
                selectedSize === opt.id
                  ? 'bg-black dark:bg-white text-white dark:text-black shadow-lg scale-[1.03]'
                  : 'bg-black/[0.03] dark:bg-white/[0.04] text-black/50 dark:text-white/50 hover:bg-black/[0.06] dark:hover:bg-white/[0.08]'
              }`}
            >
              {selectedSize === opt.id && (
                <motion.div layoutId="size-glow" className="absolute inset-0 bg-black dark:bg-white rounded-2xl" transition={{ duration: 0.3, ease: [0.76, 0, 0.24, 1] }} />
              )}
              <span className="relative z-10 text-lg font-bold font-syncopate">{opt.label}</span>
              <span className={`relative z-10 text-[7px] uppercase tracking-widest font-medium ${selectedSize === opt.id ? 'text-white/50 dark:text-black/50' : 'text-black/25 dark:text-white/25'}`}>{opt.desc}</span>
            </button>
          ))}
        </div>
      </div>}

      {/* Rarity */}
      {shopSettings.visibleFilters.rarity && <div>
        <div className="flex items-center gap-2 mb-4">
          <div className="w-7 h-7 rounded-lg bg-black/[0.04] dark:bg-white/[0.06] flex items-center justify-center">
            <Gem size={13} className="text-black/50 dark:text-white/50" />
          </div>
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-black/50 dark:text-white/50">{t.shop.rarity}</span>
        </div>
        <div className="space-y-2">
          {RARITY_OPTIONS.map((opt) => (
            <button
              key={opt.id}
              onClick={() => setSelectedRarity(selectedRarity === opt.id ? null : opt.id)}
              className={`group w-full relative flex items-center gap-3 px-4 py-3.5 rounded-2xl transition-all duration-300 overflow-hidden ${
                selectedRarity === opt.id
                  ? 'bg-black dark:bg-white text-white dark:text-black shadow-lg'
                  : 'bg-black/[0.03] dark:bg-white/[0.04] text-black/50 dark:text-white/50 hover:bg-black/[0.06] dark:hover:bg-white/[0.08]'
              }`}
            >
              {selectedRarity === opt.id && (
                <motion.div layoutId="rarity-glow" className="absolute inset-0 bg-black dark:bg-white rounded-2xl" transition={{ duration: 0.3, ease: [0.76, 0, 0.24, 1] }} />
              )}
              <span className="relative z-10 text-base">{opt.icon}</span>
              <span className="relative z-10 text-[11px] font-bold uppercase tracking-wider">{opt.label}</span>
              <span className="relative z-10 ml-auto">
                <span className={`block w-9 h-5 rounded-full transition-all duration-300 ${selectedRarity === opt.id ? 'bg-white/25 dark:bg-black/25' : 'bg-black/[0.08] dark:bg-white/[0.08]'}`}>
                  <motion.span
                    className={`block w-4 h-4 rounded-full mt-0.5 transition-colors duration-300 ${selectedRarity === opt.id ? 'bg-white dark:bg-black' : 'bg-black/20 dark:bg-white/20'}`}
                    animate={{ x: selectedRarity === opt.id ? 17 : 2 }}
                    transition={{ duration: 0.25, ease: [0.76, 0, 0.24, 1] }}
                  />
                </span>
              </span>
            </button>
          ))}
        </div>
      </div>}

      {/* Artists */}
      {allArtists.length > 0 && <div>
        <div className="flex items-center gap-2 mb-4">
          <div className="w-7 h-7 rounded-lg bg-black/[0.04] dark:bg-white/[0.06] flex items-center justify-center">
            <Palette size={13} className="text-black/50 dark:text-white/50" />
          </div>
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-black/50 dark:text-white/50">{t.nav.artists}</span>
        </div>
        <div className="space-y-1.5 max-h-[240px] overflow-y-auto pr-1">
          {allArtists.filter((a) => a.isActive).map((artist) => (
            <button
              key={artist._id}
              onClick={() => setSelectedArtistId(selectedArtistId === artist._id ? null : artist._id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 ${
                selectedArtistId === artist._id
                  ? 'bg-black dark:bg-white text-white dark:text-black'
                  : 'hover:bg-black/[0.04] dark:hover:bg-white/[0.04] text-black/60 dark:text-white/60'
              }`}
            >
              {artist.portrait && (
                <img src={artist.portrait} alt="" className="w-7 h-7 rounded-full object-cover shrink-0" />
              )}
              <span className="text-[11px] font-medium truncate text-left flex-1">{localize(artist.name)}</span>
              {selectedArtistId === artist._id && (
                <span className="w-1.5 h-1.5 rounded-full bg-white dark:bg-black shrink-0" />
              )}
            </button>
          ))}
        </div>
      </div>}
    </div>
  );

  /* =====================================================
     VIEW B — Classic minimal list with radio/check inputs
     ===================================================== */
  const listView = (
    <div className="space-y-6">
      {/* Currency */}
      <div>
        <h4 className="text-[9px] font-bold uppercase tracking-[0.3em] text-black/30 dark:text-white/30 mb-2.5">{t.shop.currency || 'Currency'}</h4>
        <div className="space-y-0.5">
          {(['USD', 'GEL'] as const).map((cur) => (
            <button
              key={cur}
              onClick={() => setCurrency(cur)}
              className={`group w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-[11px] font-medium transition-all ${
                currency === cur
                  ? 'bg-black dark:bg-white text-white dark:text-black'
                  : 'text-black/55 dark:text-white/55 hover:bg-black/[0.04] dark:hover:bg-white/[0.05]'
              }`}
            >
              <span className={`w-3.5 h-3.5 rounded-full border-[1.5px] flex items-center justify-center shrink-0 transition-all ${
                currency === cur ? 'border-white dark:border-black' : 'border-black/20 dark:border-white/20'
              }`}>
                {currency === cur && <span className="w-1.5 h-1.5 rounded-full bg-white dark:bg-black" />}
              </span>
              {cur === 'USD' ? '$ USD' : '₾ GEL'}
            </button>
          ))}
        </div>
      </div>
      {/* Price */}
      {shopSettings.visibleFilters.priceRange && <div>
        <h4 className="text-[9px] font-bold uppercase tracking-[0.3em] text-black/30 dark:text-white/30 mb-2.5">{t.shop.price}</h4>
        <div className="space-y-0.5">
          {PRICE_RANGES.map((range, idx) => (
            <button
              key={range.full}
              onClick={() => setSelectedPriceRange(selectedPriceRange === idx ? null : idx)}
              className={`group w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-[11px] font-medium transition-all ${
                selectedPriceRange === idx
                  ? 'bg-black dark:bg-white text-white dark:text-black'
                  : 'text-black/55 dark:text-white/55 hover:bg-black/[0.04] dark:hover:bg-white/[0.05]'
              }`}
            >
              <span className={`w-3.5 h-3.5 rounded-full border-[1.5px] flex items-center justify-center shrink-0 transition-all ${
                selectedPriceRange === idx ? 'border-white dark:border-black' : 'border-black/20 dark:border-white/20 group-hover:border-black/40 dark:group-hover:border-white/40'
              }`}>
                {selectedPriceRange === idx && <span className="w-1.5 h-1.5 rounded-full bg-white dark:bg-black" />}
              </span>
              {range.full}
            </button>
          ))}
        </div>
      </div>}

      {/* Size */}
      {shopSettings.visibleFilters.size && <div>
        <h4 className="text-[9px] font-bold uppercase tracking-[0.3em] text-black/30 dark:text-white/30 mb-2.5">{t.shop.size}</h4>
        <div className="space-y-0.5">
          {SIZE_OPTIONS.map((opt) => (
            <button
              key={opt.id}
              onClick={() => setSelectedSize(selectedSize === opt.id ? null : opt.id)}
              className={`group w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-[11px] font-medium transition-all ${
                selectedSize === opt.id
                  ? 'bg-black dark:bg-white text-white dark:text-black'
                  : 'text-black/55 dark:text-white/55 hover:bg-black/[0.04] dark:hover:bg-white/[0.05]'
              }`}
            >
              <span className={`w-3.5 h-3.5 rounded-[4px] border-[1.5px] flex items-center justify-center shrink-0 transition-all ${
                selectedSize === opt.id ? 'border-white dark:border-black bg-white/20 dark:bg-black/20' : 'border-black/20 dark:border-white/20 group-hover:border-black/40 dark:group-hover:border-white/40'
              }`}>
                {selectedSize === opt.id && (
                  <svg className="w-2.5 h-2.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                )}
              </span>
              {opt.full}
            </button>
          ))}
        </div>
      </div>}

      {/* Rarity */}
      {shopSettings.visibleFilters.rarity && <div>
        <h4 className="text-[9px] font-bold uppercase tracking-[0.3em] text-black/30 dark:text-white/30 mb-2.5">{t.shop.rarity}</h4>
        <div className="space-y-0.5">
          {RARITY_OPTIONS.map((opt) => (
            <button
              key={opt.id}
              onClick={() => setSelectedRarity(selectedRarity === opt.id ? null : opt.id)}
              className={`group w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-[11px] font-medium transition-all ${
                selectedRarity === opt.id
                  ? 'bg-black dark:bg-white text-white dark:text-black'
                  : 'text-black/55 dark:text-white/55 hover:bg-black/[0.04] dark:hover:bg-white/[0.05]'
              }`}
            >
              <span className={`w-3.5 h-3.5 rounded-[4px] border-[1.5px] flex items-center justify-center shrink-0 transition-all ${
                selectedRarity === opt.id ? 'border-white dark:border-black bg-white/20 dark:bg-black/20' : 'border-black/20 dark:border-white/20 group-hover:border-black/40 dark:group-hover:border-white/40'
              }`}>
                {selectedRarity === opt.id && (
                  <svg className="w-2.5 h-2.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                )}
              </span>
              <span>{opt.label}</span>
              <span className="text-[8px] ml-auto text-black/25 dark:text-white/25 uppercase tracking-wider">{opt.desc}</span>
            </button>
          ))}
        </div>
      </div>}
    </div>
  );

  /* Shared clear button */
  const clearButton = (
    <AnimatePresence>
      {activeFilterCount > 0 && (
        <motion.button
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 10 }}
          onClick={clearFilters}
          className={`w-full mt-6 py-3.5 border border-black/[0.08] dark:border-white/[0.08] text-[10px] font-bold uppercase tracking-[0.2em] text-black/35 dark:text-white/35 hover:border-black/20 dark:hover:border-white/20 hover:text-black/70 dark:hover:text-white/70 transition-all ${
            filterView === 'cards' ? 'rounded-2xl' : 'rounded-xl'
          }`}
        >
          Clear All
        </motion.button>
      )}
    </AnimatePresence>
  );

  const saleBanner = rawShopSettings?.saleBanner;
  const showBanner = saleBanner?.enabled && saleBanner?.text;

  return (
    <div className="min-h-screen bg-white dark:bg-black transition-colors duration-700">

      {/* Sale Banner */}
      {showBanner && (
        <div
          className="pt-20 sm:pt-[72px]"
        >
          <div
            className="py-2.5 px-4 text-center text-[11px] sm:text-xs font-bold uppercase tracking-[0.15em]"
            style={{ backgroundColor: saleBanner.backgroundColor || '#000', color: saleBanner.textColor || '#fff' }}
          >
            {localize(saleBanner.text)}
          </div>
        </div>
      )}

      {/* ============ TOP BAR — single inline strip ============ */}
      <div className={`relative z-30 ${showBanner ? 'pt-6 px-4 sm:px-6 lg:px-8' : 'pt-24 px-4 sm:px-6 lg:px-8'}`}>
        <div className="max-w-[1400px] mx-auto">
          {/* Unified bar */}
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="flex items-center gap-2 py-3 relative z-30 w-full"
          >
            {/* Expandable search */}
            <motion.div
              className="relative flex items-center max-w-[calc(100vw-140px)]"
              animate={{ width: searchFocused ? 320 : 180 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            >
              {/* Animated underline */}
              <motion.div
                className="absolute bottom-0 left-0 right-0 h-px origin-left"
                animate={{
                  scaleX: searchFocused ? 1 : 0,
                  background: searchFocused ? 'currentColor' : 'transparent',
                }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              />
              <motion.div
                animate={{ rotate: searchFocused ? 90 : 0, scale: searchFocused ? 1.1 : 1 }}
                transition={{ duration: 0.3 }}
              >
                <Search size={14} className={`shrink-0 transition-colors duration-300 ${searchFocused || searchQuery ? 'text-black dark:text-white' : 'text-black/25 dark:text-white/25'}`} />
              </motion.div>
              <input
                ref={searchRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setSearchFocused(false)}
                placeholder={searchFocused ? 'Artworks, artists, styles...' : 'Search...'}
                className="w-full pl-2.5 pr-1 py-2 bg-transparent text-[12px] font-medium outline-none placeholder:text-black/20 dark:placeholder:text-white/20 text-black dark:text-white"
              />
              <AnimatePresence>
                {searchQuery && (
                  <motion.button
                    initial={{ opacity: 0, scale: 0.5 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.5 }}
                    transition={{ duration: 0.15 }}
                    onClick={() => { setSearchQuery(''); searchRef.current?.focus(); }}
                    className="shrink-0 w-5 h-5 rounded-full bg-black/8 dark:bg-white/8 flex items-center justify-center hover:bg-black/15 dark:hover:bg-white/15 transition-colors"
                  >
                    <X size={9} />
                  </motion.button>
                )}
              </AnimatePresence>
              {/* Static underline */}
              <div className={`absolute bottom-0 left-0 right-0 h-px transition-colors duration-300 ${searchFocused ? 'bg-black dark:bg-white' : 'bg-black/8 dark:bg-white/8'}`} />
            </motion.div>

            {/* Thin separator — desktop only */}
            <div className="w-px h-4 bg-black/6 dark:bg-white/6 shrink-0 mx-1 hidden sm:block" />

            {/* Categories — desktop only (mobile gets its own row below) */}
            {shopSettings.visibleFilters.category && (() => {
              const needsMore = activeCategories.length > MAX_VISIBLE_CATEGORIES;
              const visibleCats = needsMore ? activeCategories.slice(0, MAX_VISIBLE_CATEGORIES) : activeCategories;
              const overflowCats = needsMore ? activeCategories.slice(MAX_VISIBLE_CATEGORIES) : [];
              // If active category is in overflow, check so we can highlight "More"
              const activeInOverflow = overflowCats.some((c) => c._id === activeCategoryId);

              const renderCatPill = (cat: Category, inDropdown = false) => {
                const isCatActive = activeCategoryId === cat._id;
                const isGiftCard = cat._id === 'cat_giftcard';
                return (
                  <button
                    key={cat._id}
                    onClick={() => {
                      setActiveCategoryId(isCatActive ? null : cat._id);
                      setActiveSubcategoryId(null);
                      if (inDropdown) setMoreCatsOpen(false);
                    }}
                    className={inDropdown
                      ? `w-full text-left px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.1em] whitespace-nowrap transition-all ${
                          isCatActive
                            ? 'bg-black dark:bg-white text-white dark:text-black'
                            : isGiftCard
                              ? 'text-amber-600 dark:text-amber-400 hover:bg-black/[0.04] dark:hover:bg-white/[0.04]'
                              : 'text-black/50 dark:text-white/50 hover:bg-black/[0.04] dark:hover:bg-white/[0.04] hover:text-black dark:hover:text-white'
                        }`
                      : `relative px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.1em] whitespace-nowrap transition-colors duration-300 shrink-0 ${
                          isGiftCard && !isCatActive ? 'border border-amber-400/40 dark:border-amber-400/30 rounded-full' : ''
                        }`
                    }
                  >
                    {!inDropdown && isCatActive && (
                      <motion.div
                        layoutId="cat-active"
                        className={`absolute inset-0 rounded-full ${isGiftCard ? 'bg-gradient-to-r from-amber-500 to-orange-500' : 'bg-black dark:bg-white'}`}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                      />
                    )}
                    <span className={inDropdown
                      ? 'flex items-center gap-1.5'
                      : `relative z-10 flex items-center gap-1.5 transition-colors duration-300 ${
                          isCatActive
                            ? 'text-white dark:text-white'
                            : isGiftCard
                              ? 'text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300'
                              : 'text-black/30 dark:text-white/30 hover:text-black/60 dark:hover:text-white/60'
                        }`
                    }>
                      {isGiftCard && <Gift size={12} />}
                      {localize(cat.name)}
                    </span>
                  </button>
                );
              };

              return (
                <div className="hidden sm:flex items-center gap-1 flex-1 min-w-0">
                  {/* Scrollable category pills */}
                  <div className="flex items-center gap-1 flex-1 min-w-0 -mx-1 px-1 overflow-x-auto scrollbar-hide">
                    {/* "All" pill */}
                    <button
                      onClick={() => { setActiveCategoryId(null); setActiveSubcategoryId(null); }}
                      className="relative px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.1em] whitespace-nowrap transition-colors duration-300 shrink-0"
                    >
                      {!activeCategoryId && (
                        <motion.div
                          layoutId="cat-active"
                          className="absolute inset-0 bg-black dark:bg-white rounded-full"
                          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                        />
                      )}
                      <span className={`relative z-10 transition-colors duration-300 ${
                        !activeCategoryId
                          ? 'text-white dark:text-black'
                          : 'text-black/30 dark:text-white/30 hover:text-black/60 dark:hover:text-white/60'
                      }`}>
                        {t.shop.all}
                      </span>
                    </button>

                    {/* Visible category pills */}
                    {visibleCats.map((cat) => renderCatPill(cat))}
                  </div>

                  {/* "More" dropdown — button here, menu rendered via portal */}
                  {needsMore && (
                    <div className="shrink-0" ref={moreCatsRef}>
                      <button
                        ref={moreCatsBtnRef}
                        onClick={() => setMoreCatsOpen(!moreCatsOpen)}
                        className={`relative flex items-center gap-1 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.1em] whitespace-nowrap transition-colors duration-300 shrink-0 rounded-full ${
                          activeInOverflow
                            ? 'bg-black dark:bg-white text-white dark:text-black'
                            : 'text-black/30 dark:text-white/30 hover:text-black/60 dark:hover:text-white/60'
                        }`}
                      >
                        <MoreHorizontal size={14} />
                        <span>+{overflowCats.length}</span>
                        <ChevronDown size={9} className={`transition-transform duration-300 ${moreCatsOpen ? 'rotate-180' : ''}`} />
                      </button>
                      {moreCatsOpen && createPortal(
                        <div
                          className="fixed inset-0 z-[9999]"
                          onClick={() => setMoreCatsOpen(false)}
                        >
                          <motion.div
                            initial={{ opacity: 0, y: -4, scale: 0.97 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
                            className="fixed min-w-[180px] py-1.5 bg-white dark:bg-[#111] border border-black/[0.06] dark:border-white/[0.06] rounded-xl shadow-xl backdrop-blur-xl"
                            style={{
                              top: getDropdownPos(moreCatsBtnRef, 'right').top,
                              right: window.innerWidth - getDropdownPos(moreCatsBtnRef, 'right').left,
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {overflowCats.map((cat) => renderCatPill(cat, true))}
                          </motion.div>
                        </div>,
                        document.body,
                      )}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Thin separator */}
            <div className="w-px h-4 bg-black/6 dark:bg-white/6 shrink-0 mx-1 hidden sm:block" />

            {/* Sort — minimal inline, dropdown via portal */}
            <div className="shrink-0 hidden sm:block">
              <button
                ref={sortBtnRef}
                onClick={(e) => { e.stopPropagation(); setSortOpen(!sortOpen); }}
                className="group flex items-center gap-1.5 px-2 py-1.5 text-[10px] font-bold uppercase tracking-[0.1em] text-black/30 dark:text-white/30 hover:text-black/60 dark:hover:text-white/60 transition-colors"
              >
                <ArrowUpDown size={11} className="opacity-50 group-hover:opacity-100 transition-opacity" />
                <span className="hidden md:inline">{currentSort?.label ?? 'Sort'}</span>
                <ChevronDown size={9} className={`transition-transform duration-300 ${sortOpen ? 'rotate-180' : ''}`} />
              </button>
              {sortOpen && createPortal(
                <div
                  className="fixed inset-0 z-[9999]"
                  onClick={() => setSortOpen(false)}
                >
                  <motion.div
                    initial={{ opacity: 0, y: -4, scale: 0.97 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
                    className="fixed w-48 py-1.5 bg-white dark:bg-[#111] border border-black/[0.06] dark:border-white/[0.06] rounded-xl shadow-xl backdrop-blur-xl"
                    style={{
                      top: getDropdownPos(sortBtnRef, 'right').top,
                      right: window.innerWidth - getDropdownPos(sortBtnRef, 'right').left,
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {SORT_OPTIONS.map((sort) => (
                      <button
                        key={sort.id}
                        onClick={() => { setSortBy(sort.id); setSortOpen(false); }}
                        className={`w-full text-left px-4 py-2.5 text-[11px] font-medium transition-all ${sortBy === sort.id ? 'text-black dark:text-white bg-black/[0.04] dark:bg-white/[0.06]' : 'text-black/40 dark:text-white/40 hover:bg-black/[0.03] dark:hover:bg-white/[0.03]'}`}
                      >
                        {sort.label}
                      </button>
                    ))}
                  </motion.div>
                </div>,
                document.body,
              )}
            </div>

            {/* Count */}
            <motion.span
              key={filteredItems.length}
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              className="hidden sm:block text-[9px] font-bold uppercase tracking-[0.2em] text-black/15 dark:text-white/15 tabular-nums shrink-0"
            >
              {filteredItems.length}
            </motion.span>

            {/* Thin separator */}
            <div className="w-px h-4 bg-black/6 dark:bg-white/6 shrink-0 mx-1 hidden sm:block" />

            {/* Grid toggle */}
            <div className="hidden sm:flex items-center relative">
              {GRID_OPTIONS.map((opt) => (
                <button
                  key={opt.cols}
                  onClick={() => setGridCols(opt.cols)}
                  className="relative w-7 h-7 flex items-center justify-center shrink-0"
                  title={opt.label}
                >
                  {gridCols === opt.cols && (
                    <motion.div
                      layoutId="grid-active-bg"
                      className="absolute inset-0.5 rounded-md bg-black dark:bg-white"
                      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    />
                  )}
                  <span className={`relative z-10 transition-colors duration-300 ${
                    gridCols === opt.cols
                      ? 'text-white dark:text-black'
                      : 'text-black/20 dark:text-white/20 hover:text-black/50 dark:hover:text-white/50'
                  }`}>
                    {opt.icon}
                  </span>
                </button>
              ))}
            </div>

            {/* Mobile filter toggle */}
            <button
              onClick={() => setShowMobileFilters(!showMobileFilters)}
              className="lg:hidden flex items-center gap-1.5 px-2 py-1.5 text-black/30 dark:text-white/30 shrink-0"
            >
              <SlidersHorizontal size={13} />
              {activeFilterCount > 0 && (
                <span className="w-4 h-4 bg-black dark:bg-white text-white dark:text-black rounded-full text-[8px] font-bold flex items-center justify-center">{activeFilterCount}</span>
              )}
            </button>
          </motion.div>

          {/* Subtle bottom line */}
          <div className="h-px bg-black/[0.04] dark:bg-white/[0.04]" />

          {/* Mobile category row — only on small screens */}
          {shopSettings.visibleFilters.category && activeCategories.length > 0 && (
            <div className="sm:hidden relative py-2">
              {/* Left fade + arrow */}
              {canScrollLeft && (
                <button
                  onClick={() => scrollMobileCats('left')}
                  className="absolute left-0 top-0 bottom-0 z-10 w-8 flex items-center justify-start bg-gradient-to-r from-white via-white/80 to-transparent dark:from-black dark:via-black/80"
                >
                  <ChevronLeft size={14} className="text-black/40 dark:text-white/40" />
                </button>
              )}
              {/* Scrollable pills */}
              <div
                ref={mobileCatsRef}
                className="flex items-center gap-1.5 overflow-x-auto scrollbar-hide px-1"
              >
                {/* "All" pill */}
                <button
                  onClick={() => { setActiveCategoryId(null); setActiveSubcategoryId(null); }}
                  className={`shrink-0 px-3.5 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-[0.08em] transition-all ${
                    !activeCategoryId
                      ? 'bg-black dark:bg-white text-white dark:text-black'
                      : 'bg-black/[0.05] dark:bg-white/[0.07] text-black/40 dark:text-white/40'
                  }`}
                >
                  {t.shop.all}
                </button>
                {activeCategories.map((cat) => {
                  const isCatActive = activeCategoryId === cat._id;
                  const isGiftCard = cat._id === 'cat_giftcard';
                  return (
                    <button
                      key={cat._id}
                      onClick={() => {
                        setActiveCategoryId(isCatActive ? null : cat._id);
                        setActiveSubcategoryId(null);
                      }}
                      className={`shrink-0 px-3.5 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-[0.08em] transition-all flex items-center gap-1 ${
                        isCatActive
                          ? isGiftCard
                            ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white'
                            : 'bg-black dark:bg-white text-white dark:text-black'
                          : isGiftCard
                            ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 border border-amber-300/40'
                            : 'bg-black/[0.05] dark:bg-white/[0.07] text-black/40 dark:text-white/40'
                      }`}
                    >
                      {isGiftCard && <Gift size={11} />}
                      {localize(cat.name)}
                    </button>
                  );
                })}
              </div>
              {/* Right fade + arrow */}
              {canScrollRight && (
                <button
                  onClick={() => scrollMobileCats('right')}
                  className="absolute right-0 top-0 bottom-0 z-10 w-8 flex items-center justify-end bg-gradient-to-l from-white via-white/80 to-transparent dark:from-black dark:via-black/80"
                >
                  <ChevronRight size={14} className="text-black/40 dark:text-white/40" />
                </button>
              )}
            </div>
          )}

          {/* Subcategory pills — shown when a category is selected */}
          <AnimatePresence>
            {activeCategoryId && activeSubcategories.length > 0 && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.25 }}
                className="overflow-hidden"
              >
                <div className="flex items-center gap-1.5 py-2.5">
                  <span className="text-[8px] font-bold uppercase tracking-[0.3em] text-black/20 dark:text-white/20 mr-1 shrink-0">Sub:</span>
                  <button
                    onClick={() => setActiveSubcategoryId(null)}
                    className={`px-2.5 py-1 text-[9px] font-bold uppercase tracking-[0.1em] rounded-full transition-all shrink-0 ${
                      !activeSubcategoryId
                        ? 'bg-black/10 dark:bg-white/10 text-black/70 dark:text-white/70'
                        : 'text-black/25 dark:text-white/25 hover:text-black/50 dark:hover:text-white/50'
                    }`}
                  >
                    All
                  </button>
                  {activeSubcategories.map((sub) => (
                    <button
                      key={sub._id}
                      onClick={() => setActiveSubcategoryId(activeSubcategoryId === sub._id ? null : sub._id)}
                      className={`px-2.5 py-1 text-[9px] font-bold uppercase tracking-[0.1em] rounded-full transition-all shrink-0 ${
                        activeSubcategoryId === sub._id
                          ? 'bg-black/10 dark:bg-white/10 text-black/70 dark:text-white/70'
                          : 'text-black/25 dark:text-white/25 hover:text-black/50 dark:hover:text-white/50'
                      }`}
                    >
                      {localize(sub.name)}
                    </button>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* ============ ACTIVE FILTERS CHIPS ============ */}
      <AnimatePresence>
        {activeFilterCount > 0 && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden border-b border-black/[0.04] dark:border-white/[0.04]"
          >
            <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-3">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[9px] font-bold uppercase tracking-[0.25em] text-black/20 dark:text-white/20 mr-1">Active:</span>
                {selectedSize && (
                  <button onClick={() => setSelectedSize(null)} className="flex items-center gap-1.5 px-3 py-1.5 bg-black/[0.05] dark:bg-white/[0.07] rounded-full text-[10px] font-bold uppercase tracking-wider group hover:bg-black/[0.1] dark:hover:bg-white/[0.12] transition-all">
                    {selectedSize}<X size={11} className="text-black/30 dark:text-white/30 group-hover:text-black dark:group-hover:text-white" />
                  </button>
                )}
                {selectedPriceRange !== null && (
                  <button onClick={() => setSelectedPriceRange(null)} className="flex items-center gap-1.5 px-3 py-1.5 bg-black/[0.05] dark:bg-white/[0.07] rounded-full text-[10px] font-bold uppercase tracking-wider group hover:bg-black/[0.1] dark:hover:bg-white/[0.12] transition-all">
                    {PRICE_RANGES[selectedPriceRange]?.label}<X size={11} className="text-black/30 dark:text-white/30 group-hover:text-black dark:group-hover:text-white" />
                  </button>
                )}
                {selectedRarity && (
                  <button onClick={() => setSelectedRarity(null)} className="flex items-center gap-1.5 px-3 py-1.5 bg-black/[0.05] dark:bg-white/[0.07] rounded-full text-[10px] font-bold uppercase tracking-wider group hover:bg-black/[0.1] dark:hover:bg-white/[0.12] transition-all">
                    {selectedRarity}<X size={11} className="text-black/30 dark:text-white/30 group-hover:text-black dark:group-hover:text-white" />
                  </button>
                )}
                {selectedArtistId && (
                  <button onClick={() => setSelectedArtistId(null)} className="flex items-center gap-1.5 px-3 py-1.5 bg-black/[0.05] dark:bg-white/[0.07] rounded-full text-[10px] font-bold uppercase tracking-wider group hover:bg-black/[0.1] dark:hover:bg-white/[0.12] transition-all">
                    {localize(allArtists.find((a) => a._id === selectedArtistId)?.name ?? { en: '', ka: '' })}<X size={11} className="text-black/30 dark:text-white/30 group-hover:text-black dark:group-hover:text-white" />
                  </button>
                )}
                <button onClick={clearFilters} className="text-[9px] font-bold uppercase tracking-[0.2em] text-black/25 dark:text-white/25 hover:text-black dark:hover:text-white transition-colors ml-2">Clear all</button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ============ MAIN: LEFT Sidebar + Grid ============ */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="flex gap-10 pt-8">

          {/* LEFT Sidebar */}
          <aside className="hidden lg:block w-[260px] shrink-0 sticky top-28 self-start">
            <div className="pr-8 border-r border-black/[0.05] dark:border-white/[0.05]">
              {/* Header with view toggle */}
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-[11px] font-bold uppercase tracking-[0.2em] flex items-center gap-2">
                  <SlidersHorizontal size={14} className="text-black/40 dark:text-white/40" />
                  {t.shop.filters}
                  {activeFilterCount > 0 && (
                    <span className="w-5 h-5 bg-black dark:bg-white text-white dark:text-black rounded-full text-[8px] font-bold flex items-center justify-center">{activeFilterCount}</span>
                  )}
                </h3>
                {/* View switch */}
                <div className="flex items-center bg-black/[0.04] dark:bg-white/[0.05] rounded-lg p-0.5">
                  <button
                    onClick={() => setFilterView('cards')}
                    className={`w-7 h-7 rounded-md flex items-center justify-center transition-all duration-300 ${
                      filterView === 'cards'
                        ? 'bg-black dark:bg-white text-white dark:text-black shadow-sm'
                        : 'text-black/30 dark:text-white/30 hover:text-black/60 dark:hover:text-white/60'
                    }`}
                    title="Card view"
                  >
                    <LayoutGrid size={12} />
                  </button>
                  <button
                    onClick={() => setFilterView('list')}
                    className={`w-7 h-7 rounded-md flex items-center justify-center transition-all duration-300 ${
                      filterView === 'list'
                        ? 'bg-black dark:bg-white text-white dark:text-black shadow-sm'
                        : 'text-black/30 dark:text-white/30 hover:text-black/60 dark:hover:text-white/60'
                    }`}
                    title="List view"
                  >
                    <List size={12} />
                  </button>
                </div>
              </div>

              {/* Filter content — animated swap */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={filterView}
                  initial={{ opacity: 0, x: filterView === 'cards' ? -10 : 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: filterView === 'cards' ? 10 : -10 }}
                  transition={{ duration: 0.25, ease: [0.76, 0, 0.24, 1] }}
                >
                  {filterView === 'cards' ? cardsView : listView}
                </motion.div>
              </AnimatePresence>
              {clearButton}
            </div>
          </aside>

          {/* Mobile Filters Drawer */}
          <AnimatePresence>
            {showMobileFilters && (
              <>
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[800] bg-black/20 backdrop-blur-sm lg:hidden" onClick={() => setShowMobileFilters(false)} />
                <motion.div
                  initial={{ y: '100%' }}
                  animate={{ y: 0 }}
                  exit={{ y: '100%' }}
                  transition={{ duration: 0.4, ease: [0.76, 0, 0.24, 1] }}
                  className="fixed bottom-0 left-0 right-0 z-[850] bg-white dark:bg-[#0a0a0a] rounded-t-3xl p-6 pb-10 max-h-[80vh] overflow-y-auto lg:hidden"
                >
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-sm font-bold uppercase tracking-[0.15em]">{t.shop.filters}</h3>
                    <div className="flex items-center gap-2">
                      <div className="flex items-center bg-black/[0.04] dark:bg-white/[0.05] rounded-lg p-0.5">
                        <button onClick={() => setFilterView('cards')} className={`w-7 h-7 rounded-md flex items-center justify-center transition-all ${filterView === 'cards' ? 'bg-black dark:bg-white text-white dark:text-black' : 'text-black/30 dark:text-white/30'}`}>
                          <LayoutGrid size={12} />
                        </button>
                        <button onClick={() => setFilterView('list')} className={`w-7 h-7 rounded-md flex items-center justify-center transition-all ${filterView === 'list' ? 'bg-black dark:bg-white text-white dark:text-black' : 'text-black/30 dark:text-white/30'}`}>
                          <List size={12} />
                        </button>
                      </div>
                      <button onClick={() => setShowMobileFilters(false)} className="w-9 h-9 rounded-full bg-black/5 dark:bg-white/5 flex items-center justify-center">
                        <X size={16} />
                      </button>
                    </div>
                  </div>
                  {filterView === 'cards' ? cardsView : listView}
                  {clearButton}
                  <button onClick={() => setShowMobileFilters(false)} className="w-full mt-4 py-4 bg-black dark:bg-white text-white dark:text-black text-xs font-bold uppercase tracking-[0.25em] rounded-2xl">
                    Show {filteredItems.length} Results
                  </button>
                </motion.div>
              </>
            )}
          </AnimatePresence>

          {/* Grid */}
          <main className="flex-1 min-w-0">
            <LayoutGroup>
              <motion.div layout className={`grid ${gridClass} gap-5 sm:gap-6`}>
                <AnimatePresence mode="popLayout">
                  {filteredItems.map((artwork) => (
                    <motion.div layout key={artwork._id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.4, ease: 'easeOut' }}>
                      <ArtworkCard artwork={artwork} />
                    </motion.div>
                  ))}
                </AnimatePresence>
              </motion.div>
            </LayoutGroup>

            {filteredItems.length === 0 && (
              <div className="flex flex-col items-center justify-center py-28 text-center space-y-5">
                <div className="w-20 h-20 rounded-full bg-black/[0.03] dark:bg-white/[0.03] flex items-center justify-center">
                  <Search size={28} className="text-black/15 dark:text-white/15" />
                </div>
                <div>
                  <p className="text-black/40 dark:text-white/40 font-bold uppercase tracking-[0.2em] text-[11px]">{t.shop.noResults}</p>
                  <p className="text-black/20 dark:text-white/20 text-[10px] mt-1.5 font-medium">Try adjusting your filters or search term</p>
                </div>
                <button onClick={clearFilters} className="px-7 py-3 border border-black/10 dark:border-white/10 rounded-xl text-[10px] font-bold uppercase tracking-[0.2em] text-black/40 dark:text-white/40 hover:border-black/30 dark:hover:border-white/30 hover:text-black dark:hover:text-white transition-all">
                  Clear All Filters
                </button>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default ShopPage;
